import { SidebarSimpleIcon } from "@phosphor-icons/react";
import { useCallback } from "react";
import { useLocation, useNavigate } from "react-router";
import { Button } from "~/components/ui/button";
import { useInspector } from "~/lib/stores/ui-store";
import { inspectorToggleColorClass, inspectorToggleLabel } from "~/components/inspector-indicator";
import { NotificationBell } from "~/components/notification-bell";
import { LegacyApprovalsBadge, LegacyAvatarMenu, LegacyInspectorToggle } from "~/components/app-header-legacy";
import { useUiRefactoring } from "~/lib/ui-refactoring";
import { newChatAsset } from "~/features/chat/modal-asset";
import { useChatOpenStore } from "~/lib/stores/chat-open-store";
import { originToReturnTo } from "~/lib/chat-origin";
import { useDesktopComposerHost } from "~/features/chat/use-desktop-composer-host";
import {
  useFeatureFlag,
  SHOW_INSPECTOR_FLAG,
} from "~/hooks/queries/use-feature-flags";
import { ChatTopBar } from "~/features/chat/top-bar/chat-top-bar";

/**
 * Top-right toolbar — three zones:
 *   - The Inspector toggle — a plain on/off switch, no activity dot (eddy,
 *     2026-10-26). The notification bell and the pending-approvals badge
 *     moved into Today's task list (FRONT-3147).
 *   - (FRONT-3143) The avatar menu — User Settings, System Settings, Sign out,
 *     Onboarding, theme — moved to the sidebar's foot (`sidebar-user-menu.tsx`).
 *
 * FRONT-3223: all of this is behind `eddy.ui-refactoring`. Off brings back the
 * activity-aware Inspector button, the avatar menu, the bell and the approvals
 * badge (`app-header-legacy.tsx`).
 */
export function AppHeader() {
  const { open: inspectorOpen, toggle: toggleInspector } = useInspector();
  const refactored = useUiRefactoring();
  const navigate = useNavigate();
  const location = useLocation();
  // FRONT-1073/FRONT-1080: lets a new chat start from any tab, not just
  // Inbox. Reviewed on PR #668 (Marko) — an earlier version of this button
  // owned its own InboxChatModal instance, which could end up open
  // alongside whatever modal the current page already manages (Home, Chats,
  // Files, Memory all own one), stacking two full-screen overlays. Routing
  // through the same `useChatOpenStore` + navigate-home mechanism the
  // Inspector's monitored-chats list already uses (FRONT-634) means there is
  // only ever the one modal owner (Home's launcher); FRONT-1076's SWITCH-bar
  // wiring lives there already, so nothing needed duplicating here.
  const requestOpenChat = useChatOpenStore((s) => s.requestOpenChat);
  // FRONT-1323: gates the Inspector's own toggle below, alongside the panel
  // itself in app-layout.tsx. Fails open — see SHOW_INSPECTOR_FLAG.
  const inspectorEnabled = useFeatureFlag(SHOW_INSPECTOR_FLAG, true);

  // Stable identity: ChatTopBar's window keydown effect depends on this, so
  // a plain function would re-subscribe the listener on every header render.
  //
  // FRONT-1550: carries the origin path along on the open request itself
  // (`originToReturnTo` — null exactly when already home) so closing the
  // modal comes back here instead of stranding the user on Home.
  const startNewChat = useCallback(() => {
    const asset = newChatAsset();
    const returnTo = originToReturnTo(location);
    // FRONT-1938: no explicit "close Settings" call needed here — Settings
    // and Chat live behind different routes, so `navigate("/")` below already
    // unmounts Settings (and everything it held) before Home's launcher ever
    // mounts to consume this request. An earlier version tracked an `isOpen`
    // flag for this direction and called it defensively here; nothing ever
    // read the flag (review finding, PR #1379), so it was dropped along with
    // the store.
    if (returnTo !== null) navigate("/");
    requestOpenChat(asset.sessionId, null, returnTo);
  }, [location, navigate, requestOpenChat]);

  useDesktopComposerHost((session) => {
    const returnTo = originToReturnTo(location);
    if (returnTo !== null) navigate("/");
    requestOpenChat(session, null, returnTo);
  });

  // 2026-08-14 new-chatbox-topbar: opens an EXISTING chat (a board row, the
  // ask chip) from any page — same navigate-home-if-needed + requestOpenChat
  // mechanism as `startNewChat` above, just carrying a real session id/title
  // instead of a freshly minted blank one. `useChatOpenStore`'s request
  // already supports an arbitrary session id (it was never blank-only), so
  // no store changes were needed.
  const openExistingChat = useCallback(
    (sessionId: string, title: string | null) => {
      const returnTo = originToReturnTo(location);
      if (returnTo !== null) navigate("/");
      requestOpenChat(sessionId, title, returnTo);
    },
    [location, navigate, requestOpenChat],
  );


  return (
    <header
      // The header's bottom edge must line up EXACTLY with the sidebar's rule
      // — the one under its workspace block, above the search box. That block
      // is `min-h-[60px]` (a fixed pixel height, its 58.5px of content never
      // reaches it); keep the two literals in step if either moves.
      //
      // 61, not 60, because of the framed shell: both regions start at the
      // same y, but the sidebar's rule sits INSIDE its card, so the card's own
      // 1px top border pushes that rule down to 61. This header is border-box,
      // so its bottom edge IS its total height — at 60 the two lines would
      // meet 1px apart across the gutter, which is visible where they nearly
      // touch.
      //
      // Framed shell: full hairline + radius, no bottom-only rule — the gap
      // under the header is canvas now, so a border-bottom would double up.
      className="h-[61px] rounded-[var(--frame-radius)] border border-border bg-card shadow-[var(--shadow-sm)] flex items-center px-2 sm:px-4 gap-1 sm:gap-2 shrink-0"
    >
      <ChatTopBar onNewChat={startNewChat} onOpenChat={openExistingChat} />

      {!refactored && <NotificationBell />}
      {!refactored && <LegacyApprovalsBadge inspectorEnabled={inspectorEnabled} />}

      {/*
       * The Inspector toggle: a plain on/off switch for the side panel (eddy,
       * 2026-10-26). No activity dot and no amber — background work and
       * waiting chats show in the top bar's status indicator. It is the only
       * way in (FRONT-874), so it carries its own `aria-label`, and
       * `aria-pressed` says whether the panel is open.
       */}
      {inspectorEnabled && !refactored && <LegacyInspectorToggle />}
      {inspectorEnabled && refactored && (
        <Button
          onClick={toggleInspector}
          variant="ghost"
          size="icon"
          // FRONT-2484: the Inspector is a side panel with no room beside a
          // phone-width page, so its toggle steps out of the crowded bar there.
          className={`h-8 w-8 max-sm:hidden ${inspectorToggleColorClass(inspectorOpen)}`}
          tooltip={inspectorToggleLabel(inspectorOpen)}
          aria-label="Inspector"
          aria-pressed={inspectorOpen}
        >
          <SidebarSimpleIcon size={16} weight={inspectorOpen ? "fill" : "regular"} />
        </Button>
      )}

      {!refactored && <LegacyAvatarMenu />}
    </header>
  );
}
